import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaInstagram, FaTiktok, FaGoodreads, FaAmazon } from "react-icons/fa";
import Newsletter from '../../newsletter/Newsletter';
import Footer from '../../privacy-policy/Footer';

const Parallax = styled.div`
  background-image: url("/images/parallax.jpg");
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding-top: 10px;
`;

const RoundedBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  margin: clamp(15px, 5vw, 30px);
  margin-bottom: 0;
  padding: clamp(15px, 5vw, 60px);
  border-radius: clamp(15px, 3vw, 20px);
`;

const BookContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: clamp(10px, 5vw, 60px);

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const CoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CoverImage = styled.img`
  width: 100%;
  height: auto;
  padding-bottom: 10px;

  /* Large desktops & laptops */
  max-width: 430px;

  /* Tablets landscape / small laptops */
  @media (max-width: 1200px) {
    max-width: 380px;
  }

  /* Tablets portrait */
  @media (max-width: 992px) {
    max-width: 330px;
  }

  /* Large phones */
  @media (max-width: 768px) {
    max-width: 280px;
  }

  /* Small phones */
  @media (max-width: 480px) {
    max-width: 230px;
  }
`;

const ArcInfo = styled.div`
  display: inline-flex; 
  flex-direction: column; 
  align-items: center;
  white-space: nowrap;   
  max-width: 250px;     
`;

const Button = styled.button`
  background-color: #caaeecff;
  color: white;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  font-weight: bold;
  align-self: center;
  margin-top: 15px;
  padding: 10px;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #ba9debbb;
  }
`;

const DescriptionContainer = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  color: #0360ffa2;
  text-align: center;
  font-family: 'MedievalSharp', serif;
  font-size: clamp(1.5rem, 5vw, 2.8rem);
  padding-bottom: clamp(15px, 3vw, 25px);
`;

const SpecialText = styled.p`
  font-weight: bold;
  font-style: italic;
`;

const Paragraph = styled.p`
  color: #2b60bdff;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  line-height: 1.6;
`;

const PromoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 60px;
  padding: clamp(30px, 5vw, 60px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const BuyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ItalicText = styled.span`
  font-style: italic;
`;

const TropesContainer = styled.div`
  white-space: nowrap;   

  h1 {
    color: white;
    font-family: 'Londrina Sketch', serif;
    font-size: clamp(1.5rem, 5vw, 2rem);
    text-align: center;
  }
`;

const FullBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  padding: clamp(15px, 5vw, 60px);
`;

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(5px, 3vw, 20px);
`;

const QuoteBox = styled.div`
  padding: clamp(15px, 3vw, 25px);
  text-align: center;
`;

const Arrow = styled.button`
  height: fit-content;
  background: none;
  border: none;
  font-size: clamp(2rem, 2.5vw + 1rem, 4rem);
  font-weight: bold;
  color: #3695f4ff;
  cursor: pointer;

  &:hover {
    color: #0360ffa2;
    transform: scale(1.08);
  }
`;

const ConnectContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;
  margin: clamp(15px, 5vw, 30px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const Link = styled.a`
  color: white;

  svg {
    width: clamp(40px, 5vw, 60px);
    height: auto;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.66);
    transform: scale(1.09);
  }
`;

const WhenTheButterfliesAreGone = () => {
  useEffect(() => {
    document.title = "When The Butterflies Are Gone - Anna Miki";
  }, []);

  const navigate = useNavigate();
  
  const quotes = [
    {
      text: 'A beautifully written indie book about healing and coming to terms with the past. It explores the sensitive topic of adoption in a thoughtful and honest way, which may feel uncomfortable at times, but never without purpose. The story also weaves in romantic elements, adding warmth and connection. Despite the heavier themes, it’s ultimately reassuring, this isn’t a tragic story, but one about growth, understanding, and emotional resolution.',
      author: 'N. Condor'
    },
    {
      text: 'I loved this book so much!!! I read most of it in one sitting. I really enjoyed the double POV and the story broke me and rebuilt me back up right after. Definitely recommend it!!',
      author: 'Anastasia Gerontiti'
    }
  ];

  const [currentQuote, setCurrentQuote] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote(prev => (prev + 1) % quotes.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  const handleNext = () => {
    setCurrentQuote((currentQuote + 1) % quotes.length);
  };

  const handlePrev = () => {
    setCurrentQuote((currentQuote - 1 + quotes.length) % quotes.length);
  };

  return (
    <Parallax>

      <RoundedBox>

        <BookContainer>
          
          <CoverContainer>

            <CoverImage src="/images/cover_b2.svg" alt="When The Butterflies Are Gone by Anna Miki" />
            
            <ArcInfo>

              <Paragraph><strong>Release:</strong> 10 Dec, 2025</Paragraph>
              <Paragraph><strong>ARC Status:</strong> Closed</Paragraph>

            </ArcInfo>
            
            <Button onClick={() => navigate('/when-the-butterflies-are-gone-sample')}>
              Read chapter 1
            </Button>

          </CoverContainer>

          <DescriptionContainer>

            <Title>When the butterflies are gone : A women's fiction novel with Romance</Title>

            <Paragraph>
              <SpecialText>Sometimes the hardest decision isn’t letting go. It's daring to hold on.</SpecialText>
            </Paragraph> <br/>

            <Paragraph>
              <span style={{fontWeight: "bold"}}>1999, Romania. </span>Disillusioned by her daughter’s choices, Elena vows to save her newborn granddaughter from the country’s grim orphanages. But when illness forces her to leave baby Sara in her daughter’s care, she returns to find the crib empty.
            </Paragraph> <br/>

            <Paragraph>
              <span style={{fontWeight: "bold"}}>2023, Baltimore. </span>Adopted from Romania then lost in the U.S. foster system, Sara is determined to make it on her own—until an accidental pregnancy threatens her dreams. She’s not ready for motherhood, but she can’t let history repeat itself. When the baby’s father dies, she finds the perfect adoptive parent in his brother David. He’s steady, kind, and everything she’s afraid to wish for. As their relationship turns romantic and her maternal instincts kick in, Sara must face her past before deciding whether to walk away or risk her heart for a future she never thought possible.
            </Paragraph> <br/>

            <Paragraph>
              Spanning continents and generations, this is a story about the legacy of love, the grip of fear, and the choices that define who we become.
            </Paragraph>

          </DescriptionContainer>

        </BookContainer>

      </RoundedBox>

      <PromoContainer>

        <BuyContainer>

          <Title style={{color: "rgba(255, 255, 255, 0.66)"}}>Buy the book</Title>

          <Paragraph style={{color: "white", textAlign: "center"}}>
            Buy <ItalicText>When The Butterflies Are Gone</ItalicText> if you love stories about resilience, found family, and quiet, steady love.
          </Paragraph>

          <a 
            href="https://www.amazon.com/When-butterflies-are-gone-fiction-ebook/dp/B0G21S3NWD?ref_=ast_author_dp&th=1&psc=1" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button>Shop this book on Amazon</Button>
          </a>

        </BuyContainer>

        <TropesContainer>

          <h1>➼ Slow burn romance</h1>
          <h1>➼ Found family</h1>
          <h1>➼ The journey back home</h1>
          <h1>➼ Generational trauma</h1>
          <h1>➼ Guarded heroine/ Soft hero</h1>

        </TropesContainer>

      </PromoContainer>

      <FullBox>

        <ReviewContainer>

          <Title>What Readers Say</Title>

          <CarouselContainer>

            <Arrow onClick={handlePrev}>❮</Arrow>

            <QuoteBox>
              
              <Paragraph style={{fontWeight: "bold"}}>
                {quotes[currentQuote].author} <span style={{color: "#caaeecff", marginLeft: "10px"}}>★★★★★</span>
              </Paragraph> <br/>

              <Paragraph style={{fontStyle: "italic"}}>“{quotes[currentQuote].text}”</Paragraph>

            </QuoteBox>

            <Arrow onClick={handleNext}>❯</Arrow>
          
          </CarouselContainer>

          <a 
            href="https://www.goodreads.com/book/show/243881935-when-the-butterflies-are-gone" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button>See all reviews on Goodreads</Button>
          </a>

        </ReviewContainer>

      </FullBox>

      <ConnectContainer>

        <LinksContainer>

          <Link href="https://www.instagram.com/annamiki_author/" target="_blank" rel="noopener noreferrer">
            <FaInstagram />
          </Link>

          <Link href="https://www.tiktok.com/@annamiki84" target="_blank" rel="noopener noreferrer">
            <FaTiktok />
          </Link>

          <Link href="https://www.goodreads.com/author/show/51714605.Anna_Miki" target="_blank" rel="noopener noreferrer">
            <FaGoodreads />
          </Link>

          <Link href="https://www.amazon.com/stores/Anna-Miki/author/B0DKYMFFFN" target="_blank" rel="noopener noreferrer">
            <FaAmazon />
          </Link>

        </LinksContainer>

        <Newsletter/>

      </ConnectContainer>

      <Footer />

    </Parallax>
  );
};

export default WhenTheButterfliesAreGone;
